import { gql, TypedDocumentNode } from "@apollo/client";
import {
  BlockPicture,
  EmployeeRoleNetwork,
  Onepager,
  GetOnepagerQuery,
  GetOnepagerQueryVariables,
  GetOnepagerSnapshotsQuery,
  GetOnepagerSnapshotsQueryVariables,
  AllOnepagersQuery,
  AllOnepagersQueryVariables,
  PublishOnepagerMutation,
  PublishOnepagerMutationVariables,
  UnPublishOnepagerMutation,
  UnPublishOnepagerMutationVariables,
  GetEditOnepagerQuery,
  GetEditOnepagerQueryVariables,
  CreateApprovedOnepagerMutationMutation,
  DeleteAndApproveOnepagerMutationMutation,
  DeleteAndApproveOnepagerMutationMutationVariables,
  RestoreAndApproveOnepagerMutationMutationVariables,
  RestoreAndApproveOnepagerMutationMutation,
  CopyOnepagerMutationMutation,
  CopyOnepagerMutationMutationVariables,
  EditOnepagerMutationMutation,
  EditOnepagerMutationMutationVariables,
  CreateApprovedOnepagerMutationMutationVariables,
} from "generated/graphql";

const teamOnepagerFragment: TypedDocumentNode<EmployeeRoleNetwork> = gql`
  fragment teamOnepagerFragment on EmployeeRoleNetwork {
    id
    employeeId
    employee {
      id
      fullName
      roles {
        id
        role {
          id
          name {
            id
            de
            en
            fr
            it
          }
        }
      }
    }
    externalName
    role {
      id
      de
      en
      fr
      it
    }
  }
`;

const blockPictureFragment: TypedDocumentNode<BlockPicture> = gql`
  fragment blockPictureFragment on BlockPicture {
    id
    url(width: 0)
    filename
    focusX
    focusY
  }
`;

export const onepagerShortFields: TypedDocumentNode<Onepager> = gql`
  fragment OnepagerShortFields on Onepager {
    id
    title
    browserTitle
    person
    institution
    supportedLanguages
    targetCountry
    tags {
      tagId
      id
      newText
      tag {
        id
        name {
          id
          de
          en
          fr
          it
        }
      }
    }
  }
`;

export const onepagerFragment: TypedDocumentNode<Onepager> = gql`
  ${onepagerShortFields}
  ${teamOnepagerFragment}
  ${blockPictureFragment}

  fragment OnepagerFields on Onepager {
    ...OnepagerShortFields
    blocks {
      __typename
      ... on IntroBlock {
        id
        type
        welcome {
          id
          de
          en
          fr
          it
        }
        intro {
          id
          de
          en
          fr
          it
        }
        navigationTitle {
          id
          de
          en
          fr
          it
        }
        logo {
          ...blockPictureFragment
        }
        hideTbfLogo
        externalLinks {
          id
          name {
            id
            de
            en
            fr
            it
          }
          url
        }
      }
      ... on JokerBlock {
        id
        type
        navigationTitle {
          id
          de
          en
          fr
          it
        }
        externalLinks {
          id
          name {
            id
            de
            en
            fr
            it
          }
          url
        }
        elements {
          __typename
          ... on RichtextElement {
            id
            type
            data {
              id
              de
              en
              fr
              it
            }
          }
          ... on PictureElement {
            id
            type
            caption {
              id
              de
              en
              fr
              it
            }
            orientation
            picture {
              ...blockPictureFragment
            }
          }
          ... on EmptyElement {
            id
            type
          }
        }
        layout {
          id
          type
          index
          indexes
        }
      }
      ... on NetworkBlock {
        id
        type
        title {
          id
          de
          en
          fr
          it
        }
        intro {
          id
          de
          en
          fr
          it
        }
        navigationTitle {
          id
          de
          en
          fr
          it
        }
        centerType
        centerText {
          id
          de
          en
          fr
          it
        }
        centerLogo {
          ...blockPictureFragment
        }
        teamLevel1 {
          ...teamOnepagerFragment
        }
        teamLevel2 {
          ...teamOnepagerFragment
        }
        teamLevel3 {
          ...teamOnepagerFragment
        }
        externalLinks {
          id
          name {
            id
            de
            en
            fr
            it
          }
          url
        }
      }
      ... on IntermissionBlock {
        id
        type
        title {
          id
          de
          en
          fr
          it
        }
        text {
          id
          de
          en
          fr
          it
        }
        pictureKey
        externalLinks {
          id
          name {
            id
            de
            en
            fr
            it
          }
          url
        }
      }
      ... on ReferenceBlock {
        id
        type
        referenceId
        navigationTitle {
          id
          de
          en
          fr
          it
        }
        title {
          id
          de
          en
          fr
          it
        }
        headline {
          id
          de
          en
          fr
          it
        }
        from
        to
        gallery {
          id
          caption {
            id
            de
            en
            fr
            it
          }
          captionPosition
          orientation
          pictureId
          picture {
            ...blockPictureFragment
          }
        }
        picture {
          ...blockPictureFragment
        }
        siaPhases
        hoaiPhases
        frPhases
        itPhases
        primaryPhase
        applicationAreas
        services
        team {
          ...teamOnepagerFragment
        }
        facts {
          id
          value {
            id
            de
            en
            fr
            it
          }
          newText
          keyId
          key {
            id
            name {
              id
              de
              en
              fr
              it
            }
          }
        }
        externalLinks {
          id
          name {
            id
            de
            en
            fr
            it
          }
          url
        }
        linkedFields
        exportedFields
      }
      ... on OutroBlock {
        id
        type
        trustedAdvisorId
        trustedAdvisor {
          id
          fullName
        }
        navigationTitle {
          id
          de
          en
          fr
          it
        }
        linkedFields
        externalLinks {
          id
          name {
            id
            de
            en
            fr
            it
          }
          url
        }
        exportedFields
        message {
          id
          de
          en
          fr
          it
        }
      }
      ... on AboutTbfBlock {
        id
        type
        navigationTitle {
          id
          de
          en
          fr
          it
        }
        intro {
          id
          de
          en
          fr
          it
        }
        showRosette
        facts {
          id
          type
          itemKey {
            id
            de
            en
            fr
            it
          }
          value {
            id
            de
            en
            fr
            it
          }
        }
        externalLinks {
          id
          name {
            id
            de
            en
            fr
            it
          }
          url
        }
        linkedFields
        exportedFields
      }
    }
  }
`;

const metadataFragment: TypedDocumentNode<Onepager> = gql`
  fragment metadataFields on Onepager {
    modifiedBy {
      __typename
      ... on Employee {
        id
        activeDirectoryId
        fullName
      }
    }
    createdBy {
      __typename
      ... on Employee {
        id
        activeDirectoryId
        fullName
      }
    }
    updatedAt
    insertedAt
    deletedAt
  }
`;

const previousPublicationField: TypedDocumentNode<Onepager> = gql`
  ${metadataFragment}

  fragment previousPublicationField on Onepager {
    previousPublication {
      id
      token
      updatedAt
      status
      onepager {
        updatedAt
        ...metadataFields
      }
    }
  }
`;

export const publicationMetadataFragment: TypedDocumentNode<Onepager> = gql`
  fragment publicationMetadataFields on Onepager {
    id
    title
    version
    publication {
      id
      token
      updatedAt
      status
    }
  }
`;

export const ONEPAGER_QUERY: TypedDocumentNode<
  GetOnepagerQuery,
  GetOnepagerQueryVariables
> = gql`
  ${onepagerFragment}
  ${publicationMetadataFragment}
  ${previousPublicationField}

  query GetOnepager($id: ID!) {
    onepager(id: $id) {
      id
      ...OnepagerFields
      numSnapshots
      ...publicationMetadataFields
      ...previousPublicationField
      ...metadataFields
    }
  }
`;

export const ONEPAGER_SNAPSHOTS_QUERY: TypedDocumentNode<
  GetOnepagerSnapshotsQuery,
  GetOnepagerSnapshotsQueryVariables
> = gql`
  ${publicationMetadataFragment}
  ${metadataFragment}

  query GetOnepagerSnapshots($id: ID!) {
    onepager(id: $id) {
      ...publicationMetadataFields
      ...metadataFields
      snapshots {
        ...publicationMetadataFields
        ...metadataFields
      }
    }
  }
`;

export const ALL_ONEPAGERS_QUERY: TypedDocumentNode<
  AllOnepagersQuery,
  AllOnepagersQueryVariables
> = gql`
  ${onepagerShortFields}
  ${publicationMetadataFragment}
  ${metadataFragment}
  ${previousPublicationField}

  query AllOnepagers($offset: Int, $limit: Int, $onepagerType: String!) {
    allOnepagers(offset: $offset, limit: $limit, onepagerType: $onepagerType) {
      approvedAt
      ...OnepagerShortFields
      ...metadataFields
      ...publicationMetadataFields
      ...previousPublicationField
    }
  }
`;

export const PUBLISH_ONEPAGER_MUTATION: TypedDocumentNode<
  PublishOnepagerMutation,
  PublishOnepagerMutationVariables
> = gql`
  mutation PublishOnepager($id: ID!) {
    publishOnepager(id: $id) {
      id
      token
      onepagerId
    }
  }
`;

export const UN_PUBLISH_ONEPAGER_MUTATION: TypedDocumentNode<
  UnPublishOnepagerMutation,
  UnPublishOnepagerMutationVariables
> = gql`
  mutation UnPublishOnepager($id: ID!) {
    unPublishOnepager(id: $id) {
      id
    }
  }
`;

export const EDIT_ONEPAGER_QUERY: TypedDocumentNode<
  GetEditOnepagerQuery,
  GetEditOnepagerQueryVariables
> = gql`
  ${onepagerFragment}

  query GetEditOnepager($id: ID!) {
    onepager(id: $id) {
      id
      ...OnepagerFields
    }
  }
`;

export const CREATE_APPROVED_ONEPAGER_MUTATION: TypedDocumentNode<
  CreateApprovedOnepagerMutationMutation,
  CreateApprovedOnepagerMutationMutationVariables
> = gql`
  ${onepagerFragment}

  mutation CreateApprovedOnepagerMutation($fields: InputOnepager!) {
    createApprovedOnepager(fields: $fields) {
      ...OnepagerFields
    }
  }
`;

export const DELETE_AND_APPROVE_ONEPAGER_MUTATION: TypedDocumentNode<
  DeleteAndApproveOnepagerMutationMutation,
  DeleteAndApproveOnepagerMutationMutationVariables
> = gql`
  ${onepagerFragment}

  mutation DeleteAndApproveOnepagerMutation($id: ID!) {
    deleteAndApproveOnepager(id: $id) {
      ...OnepagerFields
    }
  }
`;

export const RESTORE_AND_APPROVE_ONEPAGER_MUTATION: TypedDocumentNode<
  RestoreAndApproveOnepagerMutationMutation,
  RestoreAndApproveOnepagerMutationMutationVariables
> = gql`
  ${onepagerFragment}

  mutation RestoreAndApproveOnepagerMutation($id: ID!) {
    restoreDeletedOnepager(id: $id) {
      ...OnepagerFields
    }
  }
`;

export const COPY_ONEPAGER_MUTATION: TypedDocumentNode<
  CopyOnepagerMutationMutation,
  CopyOnepagerMutationMutationVariables
> = gql`
  ${onepagerFragment}

  mutation CopyOnepagerMutation($id: ID!, $fields: InputOnepager) {
    copyOnepager(id: $id, fields: $fields) {
      ...OnepagerFields
    }
  }
`;

export const EDIT_ONEPAGER_MUTATION: TypedDocumentNode<
  EditOnepagerMutationMutation,
  EditOnepagerMutationMutationVariables
> = gql`
  ${onepagerFragment}

  mutation EditOnepagerMutation($id: ID!, $fields: InputOnepager!) {
    editAndApproveOnepager(id: $id, fields: $fields) {
      ...OnepagerFields
      approvedBy {
        __typename
      }
    }
  }
`;
